import * as string from "./string";

export function init(count: number, initialValue: any) {
    return Array(count)
        .join(",")
        .split(",")
        .map(() => initialValue);
}

export function order<T = any>(
    array: T[],
    predicate: (arg0: any) => any,
    clone = true
): T[] {
    if (clone) array = array.concat();
    return array.sort((a, b) => handleOrderPredicate(a, b, predicate, "asc"));
}

export function orderDesc(
    array: any[],
    predicate: (arg0: any) => any,
    clone = true
) {
    if (clone) array = array.concat();
    return array.sort((a, b) => handleOrderPredicate(b, a, predicate, "desc"));
}

export function sort(
    array: any[],
    predicate: (arg0: any) => any,
    clone = true
) {
    if (clone) array = array.concat();
    return array.sort((a, b) => handleOrderPredicate(a, b, predicate, "prop"));
}

export function isSorted(array: any[], predicate: any, details = false) {
    const sortedClone = sort(array, predicate, true);
    for (const item of array) {
        const originalIndex = array.indexOf(item);
        const expectedIndex = sortedClone.indexOf(item);
        if (originalIndex !== expectedIndex) {
            if (details === true)
                return {
                    result: false,
                    actualIndex: originalIndex,
                    expectedIndex: expectedIndex,
                };
            else false;
        }
    }
    return details === true ? { result: true } : true;
}

export function handleOrderPredicate(
    a: any,
    b: any,
    predicate: (arg0: any) => any,
    mode: string
) {
    const aResult = predicate(a);
    const bResult = predicate(b);

    if (
        Array.isArray(aResult) &&
        Array.isArray(bResult) &&
        aResult.length === bResult.length
    ) {
        for (let i = 0; i < aResult.length; i++) {
            const aItem = aResult[i];
            const bItem = bResult[i];

            let aValue;
            let bValue;

            if (
                (typeof aItem === "number" && typeof bItem === "number") ||
                (typeof aItem === "string" && typeof bItem === "string")
            ) {
                aValue = aItem;
                bValue = bItem;
            } else if (
                aItem != null &&
                bItem != null &&
                typeof aItem === "object" &&
                typeof bItem === "object" &&
                aItem.hasOwnProperty("value") &&
                bItem.hasOwnProperty("value")
            ) {
                aValue = aItem.value;
                bValue = bItem.value;
            }

            let order;

            if (
                mode === "prop" &&
                typeof aItem.order === "string" &&
                aItem.order === bItem.order &&
                aItem.order.length > 0
            ) {
                order = aItem.order;
            } else {
                order = mode;
            }

            order = (typeof order === "string" ? order : "").toLowerCase();

            switch (order) {
                case "asc":
                case "desc":
                    break;
                default:
                    order = "asc";
            }

            if (order === "desc") {
                const temp: any = aValue;
                aValue = bValue;
                bValue = temp;
            }

            if (typeof aValue !== typeof bValue) {
                throw new Error(
                    `Value type mismatch! a[${i}] is ${typeof aValue} and b[${i}] is ${typeof bValue}`
                );
            }

            if (typeof aValue === "string") {
                const diff = string.compare(aValue, bValue);
                if (diff !== 0) return diff;
                continue;
            }

            if (aValue > bValue) {
                return 1;
            }

            if (bValue > aValue) {
                return -1;
            }
        }

        return 0;
    }
    return predicate(a) - predicate(b);
}

export function clear(array: any[]) {
    array.splice(0, array.length);
    return array;
}

export function distinct(array: any[], predicate: any = null) {
    return array.filter((v, i, s) => {
        let value = v;
        if (typeof predicate === "function") {
            value = predicate(value);
        }
        return s.indexOf(value) === i;
    });
}

export function groupBy(array: any[], predicate: any) {
    const keys = distinct(array, predicate);

    const result = [];

    for (let i = 0; i < keys.length; i++) {
        result.push({
            key: keys[i],
            items: array.filter((item) => predicate(item) === keys[i]),
        });
    }

    return result;
}

/**
 * @deprecated Use array.every instead
 */
export function all(array: any[], predicate: any) {
    return array.filter(predicate).length === array.length;
}

/**
 * @deprecated Use array.some instead
 */
export function any(array: any[], predicate: any) {
    return array.filter(predicate).length > 0;
}

export function add(array: any[], ...items: any[]) {
    addItems(array, items);
}

export function addItems<T = any>(array: T[], array2: T[]) {
    for (const item of array2) {
        array.push(item);
    }
}

export function replace(array: any[], ...items: any[]) {
    replaceItems(array, items);
}

export function replaceItems(array: any[], array2: any[]) {
    clear(array);
    addItems(array, array2);
}

export function first(array: any[], predicate: any) {
    return array.filter((v) => predicate(v))[0];
}

export function select(array: any[], predicate: any) {
    return array.map((item, index, ar) => predicate(item, index, ar));
}

export function forEach(array: any[], action: any) {
    for (const item of array) {
        action(item);
    }
}

export function insert(array: any[], index: any, value: any) {
    array.splice(index, 0, value);
    return array;
}

export function remove<T = any>(
    array: T[],
    predicate: (item: T, index: number, array: T[]) => boolean,
    clone = false
) {
    if (clone === true) array = array.concat();
    const itemsToRemove = array.filter((item, index, array) =>
        predicate(item, index, array)
    );
    for (const itemToRemove of itemsToRemove) {
        array.splice(array.indexOf(itemToRemove), 1);
    }
    return array;
}

export function subarray(
    array: any[],
    start: number,
    end: number | undefined = undefined
) {
    return array.slice(start, end);
}

export function compressValues(array: any[]) {
    if (array.length === 0) return [];
    let currentValue = array[0];
    let start = 0;
    const result = [];
    for (let i = 1; i < array.length; i++) {
        if (currentValue !== array[i]) {
            result.push({
                start: start,
                last: i - 1,
                end: i,
                value: currentValue,
                count: i - start,
            });
            currentValue = array[i];
            start = i;
        }
    }

    result.push({
        start: start,
        last: array.length - 1,
        end: array.length,
        value: currentValue,
        count: array.length - start,
    });

    return result;
}

export function map(
    array: any[],
    func: {
        (value: any, index: number, array: any[]): unknown;
        (value: any, index: number, array: any[]): unknown;
    }
) {
    if (Array.isArray(array) && typeof array.map === "function")
        return array.map(func);
    return Array.prototype.map.call(array, func);
}
